import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Map, Edit2, Trash2, Eye } from 'lucide-react';
import { Button } from '@common/Button';
import { Input } from '@common/FormFields';
import { formatDistance } from '@utils/routeHelpers';
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { db } from '@config/firebase';
import { logError, logInfo } from '@utils/logger';
import { Toast } from '@common/Toast';
import { LoadingSpinner } from '@common/LoadingSpinner';

export const RoutesTab = ({ survey }) => {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRoute, setEditingRoute] = useState(null);
  const [toast, setToast] = useState(null);

  // Fetch routes for this survey
  useEffect(() => {
    if (!survey?.id) return;

    const routesRef = collection(db, 'routes');
    const routesQuery = query(routesRef, where('surveyId', '==', survey.id));

    const unsubscribe = onSnapshot(routesQuery, 
      (snapshot) => {
        const routesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRoutes(routesData);
        setLoading(false);
        logInfo(`Loaded ${routesData.length} routes`);
      },
      (error) => {
        logError('Error loading routes:', error);
        setLoading(false);
        setToast({
          type: 'error',
          message: 'Failed to load routes'
        });
      }
    );

    return () => unsubscribe();
  }, [survey?.id]);

  const handleEditRoute = (route) => {
    setEditingRoute(route);
  };

  const handleSaveRoute = async (route, updates) => {
    try {
      const routeRef = doc(db, 'routes', route.id);
      await updateDoc(routeRef, {
        ...updates,
        updatedAt: new Date().toISOString()
      });

      setEditingRoute(null);
      setToast({
        type: 'success',
        message: 'Route updated successfully'
      });
      logInfo('Route updated successfully');
    } catch (error) {
      logError('Failed to update route:', error);
      setToast({
        type: 'error',
        message: 'Failed to update route'
      });
    }
  };

  const handleDeleteRoute = async (route) => {
    if (!window.confirm('Are you sure you want to delete this route?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'routes', route.id));
      setToast({
        type: 'success',
        message: 'Route deleted successfully'
      });
      logInfo('Route deleted successfully');
    } catch (error) {
      logError('Failed to delete route:', error);
      setToast({
        type: 'error',
        message: 'Failed to delete route'
      });
    }
  };

  const handleViewOnMap = (route) => {
    navigate('/map', {
      state: {
        surveyId: survey.id,
        routeId: route.id,
        center: route.trackPoints?.[0] ? {
          lat: route.trackPoints[0].latitude,
          lng: route.trackPoints[0].longitude
        } : null
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      {routes.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          No routes recorded yet
        </div>
      ) : (
        <div className="space-y-4">
          {routes.map((route) => (
            <div 
              key={route.id}
              className="bg-[#2A2B2E] rounded-lg p-4 border border-gray-700"
            >
              {editingRoute?.id === route.id ? (
                <div className="space-y-4">
                  <Input
                    label="Route Name"
                    defaultValue={route.name}
                    onChange={(e) => {
                      const newName = e.target.value;
                      handleSaveRoute(route, { name: newName });
                    }}
                    className="bg-gray-700 border-gray-600 text-white"
                    labelClassName="text-gray-300"
                  />
                  <div className="flex gap-2">
                    <input 
                      type="color"
                      value={route.color || '#3B82F6'}
                      onChange={(e) => {
                        const newColor = e.target.value;
                        handleSaveRoute(route, { color: newColor });
                      }}
                      className="h-8 w-8 rounded cursor-pointer"
                    />
                    <span className="text-gray-400">Route Color</span>
                  </div>
                  <Button
                    onClick={() => setEditingRoute(null)}
                    className="bg-gray-700 hover:bg-gray-600 text-white"
                  >
                    Done
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-medium">{route.name || 'Unnamed Route'}</h3>
                    <div className="text-sm text-gray-400 mt-1">
                      <span>{formatDistance(route.distance)}</span>
                      <span className="mx-2">•</span>
                      <span>{route.trackPoints?.length || 0} points</span>
                      <span className="mx-2">•</span>
                      <span>Created {new Date(route.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => handleViewOnMap(route)}
                      className="p-2 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded"
                      title="View on Map"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleEditRoute(route)}
                      className="p-2 bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 rounded"
                      title="Edit Route"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDeleteRoute(route)}
                      className="p-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded"
                      title="Delete Route"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};